import { useLocaleState } from "ra-core";
import type { ComponentProps, FormEvent, ReactNode } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDate } from "@/lib/dates";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/atomic-crm/misc/ResponsiveDialog";
import { cn } from "@/lib/utils";

/** Native select styled like the inputs (same as the ideas form). */
export const SELECT_CLASS =
  "h-10 w-full rounded-lg border border-input bg-transparent px-3 text-base outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 md:h-9 md:text-sm dark:bg-input/30";

interface FormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  formId: string;
  onSubmit: (event: FormEvent) => void;
  children: ReactNode;
  /** Footer buttons (the submit one uses `form={formId}`). */
  actions: ReactNode;
  className?: string;
}

/**
 * A market form in the app's ResponsiveDialog: a centred dialog from `md`
 * up, a full-screen liquid-glass page sheet on a phone (safe areas, swipe
 * down / Back to close, reduced motion handled by page-dialog.css).
 */
export const FormDialog = ({
  open,
  onOpenChange,
  title,
  formId,
  onSubmit,
  children,
  actions,
  className,
}: FormDialogProps) => (
  <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
    <ResponsiveDialogContent
      mobileTitle={title}
      className={cn(
        "md:max-h-[90dvh] md:overflow-y-auto sm:max-w-xl",
        className,
      )}
    >
      <DialogHeader className="max-md:sr-only">
        <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
      </DialogHeader>
      <form id={formId} onSubmit={onSubmit} className="space-y-3" noValidate>
        {children}
      </form>
      <DialogFooter className="flex-row flex-wrap gap-2 max-md:pb-2">
        {actions}
      </DialogFooter>
    </ResponsiveDialogContent>
  </ResponsiveDialog>
);

export const Field = ({
  id,
  label,
  children,
  className,
}: {
  id: string;
  label: string;
  children: ReactNode;
  className?: string;
}) => (
  <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
    <Label htmlFor={id}>{label}</Label>
    {children}
  </div>
);

/** Inline validation message tied to its input with aria-describedby. */
export const FieldError = ({
  id,
  children,
}: {
  id: string;
  children: ReactNode;
}) => (
  <p id={id} role="alert" className="text-sm text-destructive">
    {children}
  </p>
);

/**
 * Native date input. The picker renders in the browser / OS locale (often
 * mm/dd/yyyy), so the value is echoed below in the app locale, like the
 * admin kit's DateInput.
 */
export const DateField = ({
  value,
  ...props
}: Omit<ComponentProps<typeof Input>, "type" | "value"> & {
  value: string;
}) => {
  const [locale] = useLocaleState();
  const display = value
    ? formatDate(value, locale, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";
  return (
    <>
      <Input type="date" value={value} {...props} />
      {display ? (
        <p className="text-xs text-muted-foreground">{display}</p>
      ) : null}
    </>
  );
};
